import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useCallback, useReducer, useEffect } from 'react';
import { normalizeLanguageCode } from '@/lib/language';
const LANGUAGE_STORAGE_KEY = 'casalabia-current-language';
const LANGUAGE_QUERY_KEY = 'lang';
function normalizeLanguages(languages) {
    const unique = new Map();
    languages.forEach((language) => {
        const normalizedCode = normalizeLanguageCode(language.isoCode);
        if (!normalizedCode || unique.has(normalizedCode)) {
            return;
        }
        unique.set(normalizedCode, {
            ...language,
            isoCode: normalizedCode,
        });
    });
    return Array.from(unique.values());
}
const getSavedLanguage = () => {
    try {
        return normalizeLanguageCode(localStorage.getItem(LANGUAGE_STORAGE_KEY));
    }
    catch {
        return null;
    }
};
const saveLanguage = (languageCode) => {
    try {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, languageCode);
    }
    catch {
        // noop
    }
};
function getLanguageFromUrl() {
    if (typeof window === 'undefined') {
        return null;
    }
    const searchParams = new URLSearchParams(window.location.search);
    const value = searchParams.get(LANGUAGE_QUERY_KEY) ?? searchParams.get('language');
    return normalizeLanguageCode(value);
}
function syncLanguageToUrl(languageCode) {
    if (typeof window === 'undefined') {
        return;
    }
    const normalizedLanguage = normalizeLanguageCode(languageCode);
    if (!normalizedLanguage) {
        return;
    }
    const nextUrl = new URL(window.location.href);
    if (nextUrl.searchParams.get(LANGUAGE_QUERY_KEY) === normalizedLanguage) {
        return;
    }
    nextUrl.searchParams.set(LANGUAGE_QUERY_KEY, normalizedLanguage);
    window.history.replaceState(window.history.state, '', nextUrl.toString());
}
const initialState = {
    currentLanguage: 'en',
    availableLanguages: [],
    isInitialized: false,
};
const languageReducer = (state, action) => {
    switch (action.type) {
        case 'SET_LANGUAGE': {
            const normalizedLanguage = normalizeLanguageCode(action.payload);
            if (!normalizedLanguage) {
                return state;
            }
            const isLanguageAvailable = state.availableLanguages.some(lang => lang.isoCode === normalizedLanguage);
            if (!isLanguageAvailable) {
                console.warn(`Language ${normalizedLanguage} is not available in config`);
                return state;
            }
            saveLanguage(normalizedLanguage);
            return {
                ...state,
                currentLanguage: normalizedLanguage,
            };
        }
        case 'SET_AVAILABLE_LANGUAGES':
            return {
                ...state,
                availableLanguages: normalizeLanguages(action.payload),
            };
        case 'INITIALIZE': {
            const { languages, defaultLang } = action.payload;
            const normalizedLanguages = normalizeLanguages(languages);
            const urlLang = getLanguageFromUrl();
            const savedLang = getSavedLanguage();
            const normalizedDefaultLang = normalizeLanguageCode(defaultLang);
            let initialLang = 'en';
            if (urlLang && normalizedLanguages.some(lang => lang.isoCode === urlLang)) {
                initialLang = urlLang;
            }
            else if (savedLang && normalizedLanguages.some(lang => lang.isoCode === savedLang)) {
                initialLang = savedLang;
            }
            else if (normalizedDefaultLang && normalizedLanguages.some(lang => lang.isoCode === normalizedDefaultLang)) {
                initialLang = normalizedDefaultLang;
            }
            else if (normalizedLanguages.length > 0) {
                initialLang = normalizedLanguages[0].isoCode;
            }
            saveLanguage(initialLang);
            return {
                ...state,
                currentLanguage: initialLang,
                availableLanguages: normalizedLanguages,
                isInitialized: true,
            };
        }
        case 'RESET':
            return initialState;
        default:
            return state;
    }
};
const LanguageContext = createContext(undefined);
export const LanguageProvider = ({ children }) => {
    const [state, dispatch] = useReducer(languageReducer, initialState);
    const setLanguage = useCallback((languageCode) => {
        dispatch({ type: 'SET_LANGUAGE', payload: languageCode });
    }, []);
    const initializeLanguages = useCallback((languages, defaultLang) => {
        dispatch({ type: 'INITIALIZE', payload: { languages, defaultLang } });
    }, []);
    const getCurrentLanguageConfig = useCallback(() => {
        return state.availableLanguages.find(lang => lang.isoCode === state.currentLanguage) || null;
    }, [state.currentLanguage, state.availableLanguages]);
    const getLanguageByCode = useCallback((code) => {
        const normalizedCode = normalizeLanguageCode(code);
        if (!normalizedCode) {
            return null;
        }
        return state.availableLanguages.find(lang => lang.isoCode === normalizedCode) || null;
    }, [state.availableLanguages]);
    const resetLanguage = useCallback(() => {
        dispatch({ type: 'RESET' });
    }, []);
    useEffect(() => {
        if (!state.isInitialized) {
            return;
        }
        syncLanguageToUrl(state.currentLanguage);
    }, [state.currentLanguage, state.isInitialized]);
    useEffect(() => {
        if (!state.isInitialized) {
            return;
        }
        const onPopState = () => {
            const urlLanguage = getLanguageFromUrl();
            if (urlLanguage) {
                dispatch({ type: 'SET_LANGUAGE', payload: urlLanguage });
            }
        };
        window.addEventListener('popstate', onPopState);
        return () => {
            window.removeEventListener('popstate', onPopState);
        };
    }, [state.isInitialized]);
    const contextValue = {
        ...state,
        setLanguage,
        initializeLanguages,
        getCurrentLanguageConfig,
        getLanguageByCode,
        resetLanguage,
    };
    return (_jsx(LanguageContext.Provider, { value: contextValue, children: children }));
};
export const useLanguageContext = () => {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useLanguageContext should be used inside LanguageProvider');
    }
    return context;
};
